import type {
  Channel,
  ClientMessage,
  KulcinaOp,
  ServerEvent,
  ServerMessage,
} from '@movienight/shared';

/**
 * Thin WebSocket client with exponential backoff.
 *
 * On reconnect it fires `onResync` rather than trying to replay missed events.
 * At ~40 users a full refetch is a few kilobytes, and it removes an entire
 * category of desync bugs. (DESIGN.md §14)
 */

type EventHandler = (event: ServerEvent) => void;

const BACKOFF_MS = [500, 1_000, 2_000, 5_000, 10_000, 30_000] as const;

export class SocketClient {
  private ws?: WebSocket;
  private attempt = 0;
  private closed = false;
  /**
   * Subscriptions, reference-counted.
   *
   * Two components can want the same channel at once — the board subscribes to
   * every card on it, and opening a card's booking sheet subscribes to that
   * same screening. With a plain Set, closing the sheet would send one
   * `unsubscribe` and silently stop the board's hype and seat counts for that
   * card until the next reconnect. Counting is the difference between "nobody
   * is listening" and "this listener has gone".
   */
  private readonly channels = new Map<Channel, number>([['global', 1]]);
  private readonly handlers = new Set<EventHandler>();
  private resyncTimer?: number;
  /** From the `hello` frame. Not needed to draw — see useKulcina — but the
   *  presence list and the jump scare will both want to know who they are. */
  userId: string | null = null;

  constructor(private readonly onResync: () => void) {}

  connect(): void {
    this.closed = false;
    // Derived from the page, never hardcoded — that is what makes moving from
    // a bare IP to a domain a one-line change.
    const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      const reconnected = this.attempt > 0;
      this.attempt = 0;
      for (const channel of this.channels.keys()) this.send({ t: 'subscribe', channel });
      if (reconnected) this.onResync();
    };

    ws.onmessage = (e) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(e.data as string) as ServerMessage;
      } catch {
        return;
      }
      if (msg.t === 'hello') {
        this.userId = msg.userId;
        return;
      }
      if (msg.t !== 'event') return;
      const { t: _t, ...event } = msg;
      for (const handler of this.handlers) handler(event as ServerEvent);
    };

    ws.onclose = () => {
      // A socket that is no longer the current one must not schedule anything.
      // StrictMode's double mount makes this reachable in development:
      // connect / disconnect / connect leaves ws1's close firing after
      // `closed` has been reset to false, and without this guard it would
      // reconnect on top of ws2 and orphan it.
      if (this.ws !== ws || this.closed) return;
      const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)]!;
      this.attempt += 1;
      this.resyncTimer = window.setTimeout(() => this.connect(), delay);
    };

    ws.onerror = () => ws.close();
  }

  private send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  subscribe(channel: Channel): () => void {
    const held = (this.channels.get(channel) ?? 0) + 1;
    this.channels.set(channel, held);
    if (held === 1) this.send({ t: 'subscribe', channel });

    // Guarded, because React's StrictMode runs a cleanup twice in development
    // and a double release would drop the count below what is actually held.
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const left = (this.channels.get(channel) ?? 1) - 1;
      if (left > 0) {
        this.channels.set(channel, left);
        return;
      }
      this.channels.delete(channel);
      this.send({ t: 'unsubscribe', channel });
    };
  }

  /**
   * A stroke in progress on Kulčina. Fire-and-forget by design: `send` drops it
   * if the socket is not open, and that is the correct behaviour — the stroke
   * is committed over REST when the pointer comes up either way, so a missing
   * socket costs the live preview and nothing else.
   */
  draw(ops: KulcinaOp[]): void {
    if (ops.length > 0) this.send({ t: 'draw', ops });
  }

  /**
   * The hype-meter easter egg. Fire-and-forget for the same reason `draw` is:
   * nothing is written down, the sender's own card is already burning, and a
   * frame dropped because the socket is closed costs three seconds of flame on
   * other people's screens and nothing else.
   */
  engulf(screeningId: string): void {
    this.send({ t: 'engulf', screeningId });
  }

  on(handler: EventHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  disconnect(): void {
    this.closed = true;
    if (this.resyncTimer) clearTimeout(this.resyncTimer);
    this.ws?.close();
  }
}
